import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from '../locales/en.json';
import ru from '../locales/ru.json';
import it from '../locales/it.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import cs from '../locales/cs.json';
import { pluralize as pluralizeRu, pluralizeCs } from '../utils/pluralize';

const LanguageContext = createContext();

const translations = {
  en,
  ru,
  it,
  de,
  es,
  fr,
  cs,
};

// Самоназвания языков — так их показывают селекторы на любом языке интерфейса.
// Новый язык: словарь в locales, строка в translations и строка здесь
export const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'it', name: 'Italiano' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'cs', name: 'Čeština' },
];

export const LANGUAGES = LANGUAGE_OPTIONS.map(({ code }) => code);

export const languageName = (code) =>
  (LANGUAGE_OPTIONS.find((option) => option.code === code) || LANGUAGE_OPTIONS[0]).name;

// Язык по стране из geo-детекта; итальянский — и для Сан-Марино с Ватиканом,
// немецкий — для Германии, Австрии и Лихтенштейна, испанский — для Испании и
// стран Латинской Америки, французский — для Франции, Бельгии и Монако,
// чешский — для Чехии
const languageForCountry = (country) => {
  if (country === 'RU') return 'ru';
  if (['IT', 'SM', 'VA'].includes(country)) return 'it';
  if (['DE', 'AT', 'LI'].includes(country)) return 'de';
  if (['ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'UY', 'BO', 'PY', 'CR', 'PA', 'DO', 'GT', 'HN', 'NI', 'SV', 'CU'].includes(country)) return 'es';
  if (['FR', 'BE', 'MC', 'LU'].includes(country)) return 'fr';
  if (country === 'CZ') return 'cs';
  return 'en';
};

const lookup = (dictionary, key) => key.split('.').reduce((value, k) => value?.[k], dictionary);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('language');
    return LANGUAGES.includes(stored) ? stored : 'ru';
  });

  useEffect(() => {
    if (!localStorage.getItem('language')) {
      fetch('/api/geo/detect')
        .then(res => res.json())
        .then(data => {
          const detectedLang = languageForCountry(data.country);
          setLanguage(detectedLang);
          localStorage.setItem('language', detectedLang);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    let value = lookup(translations[language], key);

    // Ключа нет в переводе — показываем английский вариант, а не сам ключ
    if (value === undefined) value = lookup(translations.en, key);

    // Arrays are valid return values (e.g. t('common.days')[3])
    if (Array.isArray(value)) return value;

    // Plain objects cannot be rendered as React children — return the key as fallback
    if (value && typeof value === 'object') {
      console.warn(`Translation key "${key}" returned an object instead of a string`);
      return key;
    }

    return value || key;
  }, [language]);

  // Три формы слова нужны русскому и чешскому; в остальных языках интерфейса их две —
  // для единицы и для остальных чисел, их берем из one и many
  const pluralize = useCallback((count, one, few, many) => {
    if (language === 'ru') return pluralizeRu(count, one, few, many);
    if (language === 'cs') return pluralizeCs(count, one, few, many);
    return count === 1 ? one : many;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => LANGUAGES[(LANGUAGES.indexOf(prev) + 1) % LANGUAGES.length]);
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    pluralize,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
